import {
  GRID_ROW_NUMBER_COL_DEF,
  type GridFilterModel,
  type GridPinnedColumnFields,
  type GridSortModel,
} from '@repo/go-table';
import { action, computed, makeObservable, observable } from 'mobx';

import type { SettingsStore, TTableSettingsModel } from '../../stores/SettingsStore';

export const SERVICE_COLUMNS = [GRID_ROW_NUMBER_COL_DEF.field];

export class DisplayManager {
  @observable private _settings: TTableSettingsModel;

  constructor(private readonly settingsStore: SettingsStore) {
    const source = settingsStore.getManualAnomaliesSettings();
    this._settings = {
      columnsOrderModel: source.columnsOrderModel ?? [],
      columnsVisibility: source.columnsVisibility ?? {},
      columnsWidth: source.columnsWidth ?? {},
      sortingModel: source.sortingModel ?? [],
      filterModel: source.filterModel ?? { items: [] },
      pinnedColumns: source.pinnedColumns,
      contextColumnsHidden: source.contextColumnsHidden ?? false,
    };
    makeObservable(this);
  }

  @computed
  get settings(): TTableSettingsModel {
    return this._settings;
  }

  @computed
  get columnsVisibilityModel(): Record<string, boolean> {
    return this._settings.columnsVisibility ?? {};
  }

  @computed
  get sortingModel(): GridSortModel {
    return this._settings.sortingModel ?? [];
  }

  @computed
  get filterModel(): GridFilterModel {
    return this._settings.filterModel ?? { items: [] };
  }

  @computed
  get columnsOrderModel(): string[] {
    return this._settings.columnsOrderModel ?? [];
  }

  @computed
  get pinnedColumns(): GridPinnedColumnFields | undefined {
    return this._settings.pinnedColumns;
  }

  @computed
  get contextColumnsHidden(): boolean {
    return this._settings.contextColumnsHidden ?? false;
  }

  getColumnsWidth(): Record<string, number> | undefined {
    return this._settings.columnsWidth;
  }

  @action.bound
  private updateSettings(newSettings: TTableSettingsModel) {
    this._settings = newSettings;
    this.settingsStore.setManualAnomaliesSettings(newSettings);
  }

  @action.bound
  onColumnsOrderModelChange(columns: string[]): void {
    this.updateSettings({ ...this._settings, columnsOrderModel: columns });
  }

  @action.bound
  onSortChange(model: TTableSettingsModel['sortingModel']): void {
    this.updateSettings({ ...this._settings, sortingModel: model });
  }

  @action.bound
  onFilterChange(model: TTableSettingsModel['filterModel']): void {
    this.updateSettings({ ...this._settings, filterModel: model });
  }

  @action.bound
  onPinChange(pinnedColumns: TTableSettingsModel['pinnedColumns']): void {
    this.updateSettings({ ...this._settings, pinnedColumns });
  }

  @action.bound
  onWidthChange(incomeColumnsWidth: Record<string, number>): void {
    const newWidth = { ...this._settings.columnsWidth, ...incomeColumnsWidth };
    this.updateSettings({ ...this._settings, columnsWidth: newWidth });
  }

  @action.bound
  onColumnVisibilityChange(model: Record<string, boolean>): void {
    this.updateSettings({ ...this._settings, columnsVisibility: model });
  }

  @action.bound
  onContextColumnsHiddenChange(isHidden: boolean): void {
    this.updateSettings({ ...this._settings, contextColumnsHidden: isHidden });
  }
}
